import React from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useTenant } from '../../lib/tenant-context';
import { useLoyaltyDashboard, useLoyaltyToggle } from '../../lib/loyalty-hooks';
import { Card } from '../../components/ui/Card';
import { Users, Star, Gift, Trophy, Power, Loader2 } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
export function LoyaltyDashboardPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const {
    customers,
    loading
  } = useLoyaltyDashboard(restaurant.restaurant_id);
  const {
    isActive,
    loading: toggleLoading,
    saving,
    toggle
  } = useLoyaltyToggle(restaurant.restaurant_id);
  const totalCustomers = customers.length;
  const totalPoints = customers.reduce((sum, c) => sum + (c.current_points || 0), 0);
  const totalRewardsAvailable = customers.reduce((sum, c) => sum + (c.available_rewards_count || 0), 0);
  const totalRewardsRedeemed = customers.reduce((sum, c) => sum + (c.redeemed_rewards_count || 0), 0);
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-3844">
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-id="element-3845">
        <div data-id="element-3846">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-3847">
            Programa de Lealtad
          </h1>
          <p className="text-gray-500" data-id="element-3848">
            Gestiona tus clientes frecuentes y beneficios
          </p>
        </div>

        {/* Activation toggle */}
        <Card className={`p-6 border-2 transition-colors ${isActive ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-gray-50'}`} data-id="element-3849">
          <div className="flex items-center justify-between gap-6 flex-wrap" data-id="element-3850">
            <div className="flex items-center gap-4" data-id="element-3851">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-gray-200'}`} data-id="element-3852">
                <Power className={`w-6 h-6 ${isActive ? 'text-emerald-600' : 'text-gray-500'}`} data-id="element-3853" />
              </div>
              <div data-id="element-3854">
                <div className="flex items-center gap-2" data-id="element-3855">
                  <h2 className="font-bold text-gray-900" data-id="element-3856">
                    Sistema de Lealtad
                  </h2>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-3857">
                    {toggleLoading ? '...' : isActive ? 'ACTIVO' : 'INACTIVO'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-3858">
                  {isActive ? 'Cada pedido pagado con teléfono acumula puntos para el cliente.' : 'Los pedidos no acumulan puntos. Activa el programa para empezar a premiar a tus clientes.'}
                </p>
              </div>
            </div>

            <button onClick={() => toggle(!isActive)} disabled={saving || toggleLoading} className={`relative inline-flex items-center h-9 px-1 rounded-full transition-colors w-20 shrink-0 disabled:opacity-60 ${isActive ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-label={isActive ? 'Desactivar lealtad' : 'Activar lealtad'} data-id="element-3859">
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full bg-white shadow transform transition-transform ${isActive ? 'translate-x-11' : 'translate-x-0'}`} data-id="element-3860">
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" data-id="element-3861" />}
              </span>
            </button>
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3862">
          <Card className="p-6" data-id="element-3863">
            <div className="flex items-center gap-4" data-id="element-3864">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3865">
                <Users className="w-6 h-6 text-blue-600" data-id="element-3866" />
              </div>
              <div data-id="element-3867">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3868">
                  Clientes Leales
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3869">
                  {totalCustomers}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3870">
            <div className="flex items-center gap-4" data-id="element-3871">
              <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3872">
                <Star className="w-6 h-6 text-yellow-600" data-id="element-3873" />
              </div>
              <div data-id="element-3874">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3875">
                  Puntos Emitidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3876">
                  {totalPoints}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3877">
            <div className="flex items-center gap-4" data-id="element-3878">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3879">
                <Gift className="w-6 h-6 text-green-600" data-id="element-3880" />
              </div>
              <div data-id="element-3881">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3882">
                  Beneficios Disponibles
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3883">
                  {totalRewardsAvailable}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-6" data-id="element-3884">
            <div className="flex items-center gap-4" data-id="element-3885">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center shrink-0" data-id="element-3886">
                <Trophy className="w-6 h-6 text-purple-600" data-id="element-3887" />
              </div>
              <div data-id="element-3888">
                <p className="text-sm text-gray-500 font-medium" data-id="element-3889">
                  Beneficios Redimidos
                </p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-3890">
                  {totalRewardsRedeemed}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Customer Table */}
        <Card className="overflow-hidden" data-id="element-3891">
          <div className="p-6 border-b border-gray-200" data-id="element-3892">
            <h2 className="font-bold text-gray-900" data-id="element-3893">Directorio de Clientes</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-3894">
            <table className="w-full text-left text-sm" data-id="element-3895">
              <thead className="bg-gray-50 text-gray-500" data-id="element-3896">
                <tr data-id="element-3897">
                  <th className="px-6 py-4 font-medium" data-id="element-3898">Cliente</th>
                  <th className="px-6 py-4 font-medium" data-id="element-3899">Teléfono</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3900">Visitas</th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3901">
                    Total Gastado
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3902">
                    Ticket Promedio
                  </th>
                  <th className="px-6 py-4 font-medium text-right" data-id="element-3903">Puntos</th>
                  <th className="px-6 py-4 font-medium" data-id="element-3904">Nivel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-3905">
                {loading ? <tr data-id="element-3906">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-3907">
                      Cargando clientes...
                    </td>
                  </tr> : customers.length === 0 ? <tr data-id="element-3908">
                    <td colSpan={7} className="px-6 py-8 text-center text-gray-500" data-id="element-3909">
                      No hay clientes registrados aún.
                    </td>
                  </tr> : customers.map(customer => <tr key={customer.customer_id} className="hover:bg-gray-50" data-id="element-3910">
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-3911">
                        {customer.name || 'Sin nombre'}
                      </td>
                      <td className="px-6 py-4 text-gray-500" data-id="element-3912">
                        {customer.phone}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-3913">
                        {customer.total_orders}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-900" data-id="element-3914">
                        {formatCurrency(customer.total_spent, restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right text-gray-500" data-id="element-3915">
                        {formatCurrency(customer.total_spent / Math.max(1, customer.total_orders), restaurant.currency_symbol)}
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-brand-charcoal" data-id="element-3916">
                        {customer.current_points}
                      </td>
                      <td className="px-6 py-4" data-id="element-3917">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-3918">
                          {customer.current_level || 'Inicial'}
                        </span>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AdminLayout>;
}